import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  getAllNationalities,
  getEntitySectors,
  getPositionLevels,
} from "../../../store/structure/actions";
import { getSectors } from "../../../store/admin/actions";
import { generateReport } from "../../../store/reporting/actions";
import { getAllPlanningEntities } from "../../../store/rbm/actions";

const orgReports = [
  {
    id: "loe",
    name: "List of Employees",
    desc: "Generate detailed employee reports including: dual-employed individuals, acting employees, and active public servants filtered by institution, sector, level, age, residence, ID issuance, and more.",
    filters: [
      "report-type",
      "level",
      "sector",
      "gender",
      "civil-status",
      "planning-entity",
      "nationality",
      "age-range",
      "id-issue-place",
      "show-foreigners-only",
      "show-employee-without-id",
    ],
  },
];

const genderOptions = [
  { value: "M", label: "Male" },
  { value: "F", label: "Female" },
];

const civilStatusOptions = [
  { value: "M", label: "Married" },
  { value: "S", label: "Single" },
];

const reportTypeOptions = [
  { value: "active", label: "Active Employee" },
  { value: "acting", label: "Acting Employee" },
  { value: "dual", label: "Working in different positions" },
];

const ReportingOrgStructure = (props) => {
  const {
    loading,
    getSectors,
    getAllNationalities,
    getPositionLevels,
    generateReport,
    allPlanningEntities,
    getAllPlanningEntities,
  } = props;
  const [selectedOrgReportType, setSelectedOrgReportType] = useState(null);
  const [selectedReportType, setSelectedReportType] = useState(null);
  const [selectedPositionLevel, setSelectedPositionLevel] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedReportTo, setSelectedReportTo] = useState(null);
  const [selectedEmployeeGroup, setSelectedEmployeeGroup] = useState(null);
  const [selectedHiringMode, setSelectedHiringMode] = useState(null);
  const [reportTitle, setReportTitle] = useState("");
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedSector, setSelectedSector] = useState(null);
  const [gender, setGender] = useState(null);
  const [civilStatus, setCivilStatus] = useState(null);
  const [nationality, setNationality] = useState(null);
  const [idIssuePlace, setIdIssuePlace] = useState("");
  const [minAge, setMinAge] = useState("");
  const [maxAge, setMaxAge] = useState("");
  const [employmentStartDate, setEmploymentStartDate] = useState(null);
  const [employmentEndDate, setEmploymentEndDate] = useState(null);
  const [showForeignersOnly, setShowForeignersOnly] = useState(false);
  const [showInterns, setShowInterns] = useState(false);
  const [showHeadOfUnits, setShowHeadOfUnits] = useState(false);
  const [showEmployeeWithoutId, setShowEmployeeWithoutId] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState({
    entity: false,
    positionLevel: false,
    nationality: false,
  });
  const [sectors, setSectors] = useState([]);
  const [positionLevels, setPositionLevels] = useState([]);
  const [nationalities, setNationalities] = useState([]);

  useEffect(() => {
    if (!!selectedOrgReportType) {
      if (
        selectedOrgReportType.filters.includes("planning-entity") &&
        !allPlanningEntities.length
      ) {
        getAllPlanningEntities((status) =>
          setLoadingFilters((prev) => ({ ...prev, entity: status }))
        );
      }
      if (selectedOrgReportType.filters.includes("sector") && !sectors.length) {
        getSectors(setSectors);
      }
      if (
        selectedOrgReportType.filters.includes("level") &&
        !positionLevels.length
      ) {
        getPositionLevels((status, data) => {
          setLoadingFilters((prev) => ({ ...prev, positionLevel: status }));
          setPositionLevels(data);
        });
      }
      if (
        selectedOrgReportType.filters.includes("nationality") &&
        !positionLevels.length
      ) {
        getAllNationalities((status, data) => {
          setLoadingFilters((prev) => ({ ...prev, nationality: status }));
          setNationalities(data);
        });
      }
    }
  }, [selectedOrgReportType]);

  const resetAllStates = () => {
    setSelectedReportType(null);
    setSelectedPositionLevel(null);
    setSelectedUnit(null);
    setSelectedPosition(null);
    setSelectedReportTo(null);
    setSelectedEmployeeGroup(null);
    setSelectedHiringMode(null);
    setSelectedEntity(null);
    setSelectedSector(null);
    setGender(null);
    setCivilStatus(null);
    setNationality(null);
    setIdIssuePlace("");
    setMinAge("");
    setMaxAge("");
    setEmploymentStartDate(null);
    setEmploymentEndDate(null);
    setShowForeignersOnly(false);
    setShowInterns(false);
    setShowHeadOfUnits(false);
    setShowEmployeeWithoutId(false);
  };

  const handleGenerateReport = () => {
    const filters = {
      reportId: selectedOrgReportType?.id || "loe",
      reportType: selectedReportType?.value || null,
      levelId: selectedPositionLevel?.levelId || null,
      scaleId: selectedPositionLevel?.scaleId || null,
      gender: gender?.id || null,
      civilStatus: civilStatus?.id || null,
      nationalityId: nationality?.id || null,
      maxAge: maxAge || null,
      minAge: minAge || null,
      idIssuePlace: idIssuePlace || null,
      sectorId: selectedSector?.id || null,
      planningEntityId: selectedEntity?.id || null,
      unityId: selectedUnit?.id || null,
      positionId: selectedPosition?.id || null,
      employeeGroupId: selectedEmployeeGroup?.id || null,
      hiringModeId: selectedHiringMode?.id || null,
      reportTo: selectedReportTo?.id || null,
      employmentStartDate,
      employmentEndDate,
      showEmployeeWithoutId,
      showForeignersOnly,
      showHeadOfUnits,
      showInterns,
      reportTitle:
        reportTitle ||
        `${selectedReportType?.name} - ${
          selectedReportType?.name || "Active Employees"
        }`,
    };

    generateReport(filters, (loading, result) => {
      if (!loading && result === "success") {
      }
    });
  };

  return (
    <Paper elevation={0} className="p-4">
      <Typography variant="h6" gutterBottom>
        Generate Organization Employee Report
      </Typography>

      <div className="row">
        <div className="col-3 mb-2">
          <Autocomplete
            fullWidth
            disablePortal
            size="small"
            value={selectedOrgReportType}
            options={orgReports}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => (
              <div {...props}>
                <span>
                  <span className="d-block text-primary">{option.name}</span>
                  <small className="d-block">{option.desc}</small>
                  <Divider />
                  <Divider />
                </span>
              </div>
            )}
            onChange={(event, newValue) => {
              setSelectedOrgReportType(newValue);
              setReportTitle(newValue?.name || "");
              resetAllStates();
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Report Type"
                placeholder="Select Report Type"
              />
            )}
            loading={false}
            loadingText="Loading..."
          />
        </div>

        {!!selectedOrgReportType?.filters.includes("planning-entity") && (
          <div className="col-3">
            <Autocomplete
              loading={loadingFilters.entity}
              size="small"
              id="planning-entity"
              defaultValue={null}
              value={selectedEntity}
              options={allPlanningEntities}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(event, entity) => {
                setSelectedEntity(entity || null);
              }}
              getOptionLabel={(option) => option?.name}
              renderOption={(props, entity) => (
                <Box component="li" {...props}>
                  {entity?.name}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by entity"
                  placeholder="Filter by entity"
                />
              )}
            />
            <div className="col-12 mb-2">
              {loadingFilters.entity && <LinearProgress />}
            </div>
          </div>
        )}

        {!!selectedOrgReportType?.filters.includes("sector") && (
          <div className="col-3">
            <Autocomplete
              loading={loading}
              size="small"
              id="sector"
              defaultValue={null}
              value={selectedSector}
              options={sectors}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(event, sector) => {
                setSelectedSector(sector || null);
              }}
              getOptionLabel={(option) => option?.name}
              renderOption={(props, sector) => (
                <Box component="li" {...props}>
                  {sector?.name}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by sector"
                  placeholder="Filter by sector"
                />
              )}
            />
            <div className="col-12 mb-2">{loading && <LinearProgress />}</div>
          </div>
        )}

        {!!selectedOrgReportType?.filters.includes("gender") && (
          <div className="col-3 mb-3">
            <TextField
              select
              label="Gender"
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              fullWidth
              size="small"
            >
              {genderOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </div>
        )}

        {!!selectedOrgReportType?.filters.includes("civil-status") && (
          <div className="col-3 mb-3">
            <TextField
              select
              label="Civil Status"
              value={civilStatus}
              onChange={(e) => setCivilStatus(e.target.value)}
              fullWidth
              size="small"
            >
              {civilStatusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </div>
        )}

        {!!selectedOrgReportType?.filters.includes("nationality") && (
          <div className="col-3 mb-3">
            <Autocomplete
              loading={loadingFilters.nationality}
              size="small"
              value={nationality}
              options={nationalities}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(event, value) => setNationality(value)}
              getOptionLabel={(option) => option?.name || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Nationality"
                  placeholder="Nationality"
                />
              )}
            />
            <div className="col-12 mb-2">
              {loadingFilters.nationality && <LinearProgress />}
            </div>
          </div>
        )}

        {!!selectedOrgReportType?.filters.includes("id-issue-place") && (
          <div className="col-3 mb-3">
            <TextField
              label="ID Issue Place"
              fullWidth
              size="small"
              value={idIssuePlace}
              onChange={(e) => setIdIssuePlace(e.target.value)}
            />
          </div>
        )}

        {!!selectedOrgReportType?.filters.includes("age-range") && (
          <>
            <div className="col-2 mb-3">
              <TextField
                label="Min Age"
                type="number"
                size="small"
                value={minAge}
                onChange={(e) => setMinAge(e.target.value)}
                fullWidth
              />
            </div>
            <div className="col-2 mb-3">
              <TextField
                label="Max Age"
                type="number"
                size="small"
                value={maxAge}
                onChange={(e) => setMaxAge(e.target.value)}
                fullWidth
              />
            </div>
          </>
        )}

        {!!selectedOrgReportType?.filters.includes("show-foreigners-only") && (
          <div className="col-3 mb-3">
            <FormControlLabel
              control={
                <Checkbox
                  checked={showForeignersOnly}
                  onChange={(e) => setShowForeignersOnly(e.target.checked)}
                />
              }
              label="Show Foreigners Only"
            />
          </div>
        )}

        {!!selectedOrgReportType?.filters.includes(
          "show-employee-without-id"
        ) && (
          <div className="col-3 mb-3">
            <FormControlLabel
              control={
                <Checkbox
                  checked={showEmployeeWithoutId}
                  onChange={(e) => setShowEmployeeWithoutId(e.target.checked)}
                />
              }
              label="Show Employees Without ID"
            />
          </div>
        )}

        <div className="col-3 mb-3">
          <TextField
            label="Report Title"
            fullWidth
            value={reportTitle}
            size="small"
            onChange={(e) => setReportTitle(e.target.value)}
          />
        </div>

        <div className="col-12 mt-3">
          <Button
            variant="contained"
            color="primary"
            onClick={handleGenerateReport}
            disabled={!reportTitle || !selectedEntity || !selectedOrgReportType}
          >
            Generate Report
          </Button>
        </div>
      </div>
    </Paper>
  );
};

const mapStateToProps = ({ loading, entitySectors, allPlanningEntities }) => {
  return {
    loading,
    entitySectors,
    allPlanningEntities,
  };
};

export default connect(mapStateToProps, {
  getEntitySectors,
  getSectors,
  getAllNationalities,
  getPositionLevels,
  generateReport,
  getAllPlanningEntities,
})(ReportingOrgStructure);
